import { Directive, ElementRef, forwardRef, HostListener, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
  selector: '[appTitlecase]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TitlecaseDirective),
      multi: true,
    },
  ],
})
export class TitlecaseDirective implements ControlValueAccessor {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private _onChange = (value: string) => {
    // no-op
  };
  private _onTouched = () => {
    // no-op
  };

  private el = inject<ElementRef<HTMLInputElement>>(ElementRef);

  private get inputElement(): HTMLInputElement {
    return this.el.nativeElement;
  }

  /** ControlValueAccessor: Write value to input */
  writeValue(value: unknown): void {
    const input = this.inputElement;
    const normalizedValue = value == null ? '' : String(value);
    const formattedValue = normalizedValue ? this.toTitleCase(normalizedValue) : '';

    if (input.value !== formattedValue) {
      input.value = formattedValue;
    }
  }

  /** ControlValueAccessor: Register change callback */
  registerOnChange(fn: (_: string) => void): void {
    this._onChange = fn;
  }

  /** ControlValueAccessor: Register touched callback */
  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  /** ControlValueAccessor: Handle disabled state */
  setDisabledState(isDisabled: boolean): void {
    this.inputElement.disabled = isDisabled;
  }

  /** Converts value to title case on input while preserving the caret position */
  @HostListener('input')
  onInput() {
    const input = this.inputElement;
    const originalValue = input.value;
    const selectionStart = input.selectionStart;
    const selectionEnd = input.selectionEnd;
    const value = this.toTitleCase(originalValue);

    if (value !== originalValue) {
      input.value = value;
      if (selectionStart !== null && selectionEnd !== null) {
        input.setSelectionRange(selectionStart, selectionEnd);
      }
    }

    this._onChange(value);
  }

  /** Marks control as touched on blur */
  @HostListener('blur')
  onBlur() {
    this._onTouched();
  }

  toTitleCase(str: string): string {
    return str
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}
